import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  BellSlashIcon,
  CheckIcon,
  Cog6ToothIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  ConfirmDialog,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  dayGroup,
  formatWhen,
  NotificationIcon,
  TYPE_LABELS,
} from '../../components/notifications/notificationDisplay';
import {
  useBulkNotifications,
  useDeleteNotification,
  useDeleteNotifications,
  useMarkAllNotificationsRead,
  useMarkNotification,
  useNotificationsPage,
  useNotificationStats,
  useScanForAlerts,
} from '../../hooks/useNotifications';
import { Notification, NotificationCategory, NotificationType } from '../../services/notifications.service';
import { defaultRowsPerPage } from '../../lib/preferences';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';

const PAGE_SIZES = [10, 25, 50, 100];

type ReadFilter = 'all' | 'unread' | 'read';

const READ_TABS: { key: ReadFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'read', label: 'Read' },
];

const TYPE_OPTIONS = [NotificationType.ERROR, NotificationType.WARNING, NotificationType.INFO, NotificationType.SUCCESS];

const NotificationsList: React.FC = () => {
  const navigate = useNavigate();
  // Checking for alerts on demand needs the "Alerts: Run" permission
  const { can } = usePermissions();
  const canScan = can('notifications.execute');

  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [category, setCategory] = useState<NotificationCategory | ''>('');
  const [type, setType] = useState<NotificationType | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<null | 'read' | 'selected'>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useNotificationsPage({
    page,
    limit,
    isRead: readFilter === 'all' ? undefined : readFilter === 'read',
    category: category || undefined,
    type: type || undefined,
    search: search || undefined,
  });
  const { data: stats } = useNotificationStats();

  const markNotification = useMarkNotification();
  const markAllRead = useMarkAllNotificationsRead();
  const bulk = useBulkNotifications();
  const deleteNotification = useDeleteNotification();
  const deleteNotifications = useDeleteNotifications();
  const scan = useScanForAlerts();

  const notifications = useMemo(() => data?.data ?? [], [data]);
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  // Step back when the last page no longer exists
  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  // A selection only ever holds what is on screen
  useEffect(() => {
    setSelected((previous) => {
      const visible = new Set(notifications.map((notification) => notification.id));
      const next = new Set(Array.from(previous).filter((id) => visible.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [notifications]);

  const changeFilter = (apply: () => void) => {
    apply();
    setPage(1);
    setSelected(new Set());
  };

  const hasFilters = !!category || !!type || !!searchInput || readFilter !== 'all';
  const clearFilters = () => {
    setCategory('');
    setType('');
    setSearchInput('');
    setSearch('');
    setReadFilter('all');
    setPage(1);
  };

  const fail = (fallback: string) => (actionError: unknown) => toast.error(getApiErrorMessage(actionError, fallback));

  const open = (notification: Notification) => {
    if (!notification.isRead) markNotification.mutate({ id: notification.id, read: true });
    if (notification.actionUrl) navigate(notification.actionUrl);
  };

  const toggleSelected = (id: string) => {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const allSelected = notifications.length > 0 && notifications.every((notification) => selected.has(notification.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(notifications.map((notification) => notification.id)));

  const runBulk = (action: 'read' | 'unread' | 'delete') => {
    const ids = Array.from(selected);
    bulk.mutate(
      { ids, action },
      {
        onSuccess: (result) => {
          setSelected(new Set());
          setConfirm(null);
          toast.success(
            action === 'delete'
              ? `${result.affected} ${result.affected === 1 ? 'notification' : 'notifications'} deleted`
              : `${result.affected} marked ${action}`,
          );
        },
        onError: (actionError) => {
          setConfirm(null);
          fail('The notifications could not be changed')(actionError);
        },
      },
    );
  };

  const handleScan = () => {
    scan.mutate(undefined, {
      onSuccess: (result) =>
        toast.success(
          result.raised === 0 && result.resolved === 0
            ? 'Checked. Nothing new.'
            : `Checked: ${result.raised} new, ${result.resolved} no longer needed`,
        ),
      onError: fail('The check could not be run'),
    });
  };

  const selectedUnread = notifications.some((notification) => selected.has(notification.id) && !notification.isRead);
  const selectedRead = notifications.some((notification) => selected.has(notification.id) && notification.isRead);
  const readCount = stats ? stats.total - stats.unread : 0;

  const statCards = [
    { label: 'Unread', value: stats?.unread, hint: 'Waiting for you', tone: stats?.unread ? 'text-indigo-600' : 'text-gray-900' },
    { label: 'Today', value: stats?.today, hint: 'Arrived since midnight', tone: 'text-gray-900' },
    {
      label: 'Needs action',
      value: stats ? stats.byType.error + stats.byType.warning + (stats.byType.inventory_reorder ?? 0) : undefined,
      hint: 'Warnings and urgent',
      tone: 'text-gray-900',
    },
    { label: 'Total', value: stats?.total, hint: 'Read ones are kept 90 days', tone: 'text-gray-900' },
  ];

  // Rows under a heading for each day group, in the order they come back (newest first)
  const groups = useMemo(() => {
    const result: { label: string; items: Notification[] }[] = [];
    notifications.forEach((notification) => {
      const label = dayGroup(notification.createdAt);
      const last = result[result.length - 1];
      if (last && last.label === label) last.items.push(notification);
      else result.push({ label, items: [notification] });
    });
    return result;
  }, [notifications]);

  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);
  const iconButton = 'rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40';

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
          <p className="mt-1 text-sm text-gray-500">
            Stock running low, overdue invoices, late deliveries and other things that need attention.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link to="/settings" className={secondaryButtonClass} title="Choose which notifications you get">
            <Cog6ToothIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Preferences
          </Link>
          {canScan && (
            <button type="button" onClick={handleScan} disabled={scan.isPending} className={secondaryButtonClass}>
              <ArrowPathIcon className={cn('-ml-0.5 mr-2 h-4 w-4', scan.isPending && 'animate-spin')} />
              {scan.isPending ? 'Checking…' : 'Check now'}
            </button>
          )}
          <button
            type="button"
            onClick={() =>
              markAllRead.mutate(undefined, {
                onSuccess: (result) => toast.success(`${result.affected} marked read`),
                onError: fail('The notifications could not be marked read'),
              })
            }
            disabled={!stats?.unread || markAllRead.isPending}
            className={primaryButtonClass}
          >
            <CheckIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Mark all read
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.tone)}>{stat.value === undefined ? '—' : formatNumber(stat.value)}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        {/* Filters */}
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 xl:flex-row xl:items-center">
          <div className="inline-flex shrink-0 self-start rounded-lg bg-gray-100 p-1">
            {READ_TABS.map((tab) => {
              const count = !stats ? undefined : tab.key === 'all' ? stats.total : tab.key === 'unread' ? stats.unread : readCount;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => changeFilter(() => setReadFilter(tab.key))}
                  aria-pressed={readFilter === tab.key}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium',
                    readFilter === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                  )}
                >
                  {tab.label}
                  {count !== undefined && <span className="ml-1.5 text-xs text-gray-500 tabular-nums">{formatNumber(count)}</span>}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search notifications"
              aria-label="Search notifications"
              className={cn(inputClass, 'pl-9')}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:flex">
            <select
              value={category}
              onChange={(event) => changeFilter(() => setCategory(event.target.value as NotificationCategory | ''))}
              aria-label="Area"
              className={cn(inputClass, 'sm:w-48')}
            >
              <option value="">All areas</option>
              {Object.values(NotificationCategory).map((value) => (
                <option key={value} value={value}>
                  {CATEGORY_LABELS[value]}
                  {stats ? ` (${stats.byCategory[value] ?? 0})` : ''}
                </option>
              ))}
            </select>
            <select
              value={type}
              onChange={(event) => changeFilter(() => setType(event.target.value as NotificationType | ''))}
              aria-label="Kind"
              className={cn(inputClass, 'sm:w-44')}
            >
              <option value="">All kinds</option>
              {TYPE_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {TYPE_LABELS[value]}
                </option>
              ))}
            </select>
          </div>
          {hasFilters && (
            <button type="button" onClick={clearFilters} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              Clear
            </button>
          )}
        </div>

        {/* Select all, and what can be done with the selection */}
        <div className="flex min-h-[3rem] flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-200 bg-gray-50 px-4 py-2">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              disabled={notifications.length === 0}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            {selected.size > 0 ? `${selected.size} selected` : 'Select all on this page'}
          </label>
          {selected.size > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              {selectedUnread && (
                <button type="button" onClick={() => runBulk('read')} disabled={bulk.isPending} className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50">
                  <EnvelopeOpenIcon className="mr-1 h-4 w-4" />
                  Mark read
                </button>
              )}
              {selectedRead && (
                <button type="button" onClick={() => runBulk('unread')} disabled={bulk.isPending} className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50">
                  <EnvelopeIcon className="mr-1 h-4 w-4" />
                  Mark unread
                </button>
              )}
              <button type="button" onClick={() => setConfirm('selected')} disabled={bulk.isPending} className="inline-flex items-center rounded-md px-2 py-1 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                <TrashIcon className="mr-1 h-4 w-4" />
                Delete
              </button>
            </div>
          ) : (
            readCount > 0 && (
              <button type="button" onClick={() => setConfirm('read')} className="ml-auto text-sm font-medium text-gray-600 hover:text-red-600">
                Delete read notifications
              </button>
            )
          )}
        </div>

        {/* The notifications */}
        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The notifications could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : isLoading ? (
          <div className="space-y-4 p-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="h-12 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="px-4 py-14 text-center">
            <BellSlashIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-3 text-sm font-medium text-gray-900">
              {hasFilters ? 'No notification matches' : "You're all caught up"}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {hasFilters
                ? 'Try a different search or filter.'
                : 'Nothing needs your attention. The system checks stock, invoices, deliveries, purchases and work orders every 15 minutes.'}
            </p>
          </div>
        ) : (
          <div className={cn(isFetching && 'opacity-60')}>
            {groups.map((group) => (
              <section key={group.label}>
                <h2 className="border-b border-gray-100 bg-white px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-400">
                  {group.label}
                </h2>
                <ul className="divide-y divide-gray-100">
                  {group.items.map((notification) => (
                    <li
                      key={notification.id}
                      className={cn('flex items-start gap-3 px-4 py-3', !notification.isRead && 'bg-indigo-50/40')}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(notification.id)}
                        onChange={() => toggleSelected(notification.id)}
                        aria-label={`Select ${notification.title}`}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <NotificationIcon type={notification.type} className="mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          {!notification.isRead && <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-600" aria-label="Unread" />}
                          <p className={cn('text-sm', notification.isRead ? 'font-medium text-gray-700' : 'font-semibold text-gray-900')}>
                            {notification.title}
                          </p>
                          <span className={cn(badgeClass, CATEGORY_TONES[notification.category] ?? CATEGORY_TONES.system)}>
                            {CATEGORY_LABELS[notification.category] ?? notification.category}
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-gray-600">{notification.message}</p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                          <span title={new Date(notification.createdAt).toLocaleString('en-IN')}>{formatWhen(notification.createdAt)}</span>
                          {notification.actionUrl && (
                            <button
                              type="button"
                              onClick={() => open(notification)}
                              className="inline-flex items-center font-medium text-indigo-600 hover:text-indigo-500"
                            >
                              Open
                              <ArrowTopRightOnSquareIcon className="ml-1 h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            markNotification.mutate(
                              { id: notification.id, read: !notification.isRead },
                              { onError: fail('The notification could not be changed') },
                            )
                          }
                          disabled={markNotification.isPending}
                          title={notification.isRead ? 'Mark unread' : 'Mark read'}
                          aria-label={`${notification.isRead ? 'Mark unread' : 'Mark read'}: ${notification.title}`}
                          className={iconButton}
                        >
                          {notification.isRead ? <EnvelopeIcon className="h-4 w-4" /> : <EnvelopeOpenIcon className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            deleteNotification.mutate(notification.id, {
                              onSuccess: () => toast.success('Notification deleted'),
                              onError: fail('The notification could not be deleted'),
                            })
                          }
                          disabled={deleteNotification.isPending}
                          title="Delete"
                          aria-label={`Delete: ${notification.title}`}
                          className={cn(iconButton, 'hover:bg-red-50 hover:text-red-600')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirm !== null}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm === 'selected') runBulk('delete');
          else
            deleteNotifications.mutate('read', {
              onSuccess: (result) => {
                setConfirm(null);
                toast.success(`${result.deleted} read ${result.deleted === 1 ? 'notification' : 'notifications'} deleted`);
              },
              onError: (actionError) => {
                setConfirm(null);
                fail('The notifications could not be deleted')(actionError);
              },
            });
        }}
        title={confirm === 'selected' ? 'Delete selected notifications' : 'Delete read notifications'}
        message={
          confirm === 'selected'
            ? `Delete the ${selected.size} selected ${selected.size === 1 ? 'notification' : 'notifications'}? This cannot be undone.`
            : `Delete all ${formatNumber(readCount)} read ${readCount === 1 ? 'notification' : 'notifications'}? Unread ones are kept. This cannot be undone.`
        }
        confirmLabel="Delete"
        pendingLabel="Deleting…"
        isPending={bulk.isPending || deleteNotifications.isPending}
      />
    </div>
  );
};

export default NotificationsList;
